import { transformUnits } from '@/helpers/unit-formatter/unit-formatter';
import { FormattedUnitLabels, UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import {
  formatNumber,
  unitValueToFormattedUnitLabels,
} from '@/helpers/unit-formatter/unit-formatter.utils';

/**
 * Formatter for percentage values.
 * Formats percentage values with no space between value and '%' symbol,
 * unlike the default formatter which adds spacing.
 *
 * @param {UnitValue} unitValue - The percentage value and units to format
 * @param {any} unitValue.value - The numeric percentage value
 * @param {string} [unitValue.units] - The current units of the value (typically '%')
 * @param {string} [displayUnits] - Optional target units to transform to before formatting
 * @returns {FormattedUnitLabels} Formatted labels containing:
 *   - valueLabel: The formatted numeric value
 *   - unitsLabel: The unit string (typically '%')
 *   - valueUnitsLabel: Combined value and units WITHOUT spacing (e.g., '50%')
 *
 * @example
 * formatPercent({ value: 75.5, units: '%' })
 * // Returns: { valueLabel: '75.5', unitsLabel: '%', valueUnitsLabel: '75.5%' }
 *
 * @example
 * formatPercent({ value: 0.855, units: '' }, '%')
 * // Returns: { valueLabel: '85.5', unitsLabel: '%', valueUnitsLabel: '85.5%' }
 */
export const formatPercent = (unitValue: UnitValue, displayUnits?: string): FormattedUnitLabels => {
  const transformed = transformUnits(unitValue, displayUnits);

  const formatted = unitValueToFormattedUnitLabels({
    value: formatNumber(transformed.value),
    units: transformed.units,
  });

  return {
    valueLabel: `${formatted.valueLabel}`,
    unitsLabel: `${formatted.unitsLabel}`,
    valueUnitsLabel: `${formatted.valueLabel}${formatted.unitsLabel}`, // note this differs from the default formatter due to it's spacing of the value and units
  };
};
